import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const INTENT_COLORS = {
  purchase_intent: 'bg-green-500/20 text-green-300',
  hot_lead: 'bg-orange-500/20 text-orange-300',
  complaint: 'bg-red-500/20 text-red-300',
  needs_review: 'bg-yellow-500/20 text-yellow-300',
  asked_question: 'bg-blue-500/20 text-blue-300',
};

export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('unhandled');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/agent/bonus/inbox', { params: { status, limit: 50 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const markHandled = async (contactKey) => {
    try {
      await api.post(`/agent/bonus/inbox/${encodeURIComponent(contactKey)}/handled`);
      toast.success('Oznaczone jako obsłużone');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-semibold">AI Inbox</h1>
          <p className="text-sm text-muted-foreground">Rozmowy wymagające Twojej uwagi (purchase_intent, hot_lead, complaint, question, needs_review)</p>
        </div>
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="p-2 rounded border border-border bg-background text-sm">
            <option value="unhandled">nieobsłużone</option>
            <option value="handled">obsłużone</option>
          </select>
          <Button variant="outline" onClick={load}>Odśwież</Button>
        </div>
      </div>

      {loading ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-muted-foreground">Inbox pusty — nic nie wymaga uwagi.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">Razem: {total}</div>
          {items.map(t => (
            <Card key={t.contact_key}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{t.first_name || ''} {t.last_name || ''} <span className="text-xs text-muted-foreground font-mono">{t.phone || t.email}</span></div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(t.tags || []).filter(x => INTENT_COLORS[x]).map(tag => (
                        <span key={tag} className={`text-[10px] px-2 py-0.5 rounded ${INTENT_COLORS[tag]}`}>{tag}</span>
                      ))}
                      {(t.tags || []).filter(x => !INTENT_COLORS[x] && x !== 'handled_by_admin').slice(0, 4).map(tag => (
                        <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  {status === 'unhandled' && (
                    <Button size="sm" onClick={() => markHandled(t.contact_key)}>✓ Obsłużone</Button>
                  )}
                </div>

                {/* Ostatnie 3 wiadomości */}
                {t.messages?.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {t.messages.slice(-3).map((m, i) => (
                      <div key={i} className={`text-xs p-2 rounded ${m.direction === 'incoming' ? 'bg-muted/40' : 'bg-blue-500/10 border-l-2 border-blue-500'}`}>
                        <div className="opacity-60 text-[10px]">{m.direction}/{m.channel} · {m.ts?.slice(0, 19).replace('T', ' ')}{m.ai_generated && ' · AI'}</div>
                        <div className="mt-0.5 whitespace-pre-wrap">{m.content}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ostatnie notatki */}
                {t.agent_notes?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {t.agent_notes.slice(-2).map((n, i) => (
                      <div key={i} className="text-xs p-2 rounded bg-purple-500/10 border-l-2 border-purple-500">
                        <div className="opacity-60 text-[10px]">notatka · {n.author} · {n.ts?.slice(0, 19).replace('T', ' ')}</div>
                        <div>{n.note}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-[10px] opacity-50 mt-2">Ostatnia interakcja: {t.last_interaction_at?.slice(0, 19).replace('T', ' ')}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
